import React from 'react';
import { useIntl } from 'react-intl';
import { throttle } from 'throttle-debounce';
import BackgroundImage from 'components/background-image';
import Html from 'components/html';
import Link from 'components/link';
import NavigationDots from 'components/navigation-dots';
import { sendTracking } from 'services/google-analytics';

import tracking from './not-convinced-banner.tracking';
import { config, sites, testId } from './not-convinced-banner.settings';
import messages from './not-convinced-banner.i18n';
import styles from './not-convinced-banner.module.scss';

const translation = messages.pages.landing.notConvincedBanner.livejasmin;

interface NotConvincedBannerElement {
  curtainProgress: number;
}

const NotConvincedBanner: React.FunctionComponent<NotConvincedBannerElement> = (props) => {
  const { curtainProgress } = props;
  const [selectedSite, setSelectedSite] = React.useState(0);
  const { formatMessage } = useIntl();
  const bannerRef = React.useRef<HTMLDivElement>(null);
  const sitesRef = React.useRef<HTMLDivElement>(null);
  const curtainRef = React.useRef<HTMLDivElement>(null);
  const introRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!bannerRef.current || !curtainRef.current || !introRef.current) return;

    const { mid, max } = config.curtainProgress;

    introRef.current.style.opacity = `${1 - curtainProgress / max}`;

    const bannerHeight = bannerRef.current.clientHeight;
    const progressHeight = bannerHeight / mid;

    if (curtainProgress <= mid) {
      curtainRef.current.style.bottom = '0';
      curtainRef.current.style.height = `${progressHeight * curtainProgress}px`;
      curtainRef.current.style.top = '';

      return;
    }

    curtainRef.current.style.bottom = '';
    curtainRef.current.style.height = `${bannerHeight - progressHeight * (curtainProgress - mid)}px`;
    curtainRef.current.style.top = '0';
  }, [curtainProgress]);

  React.useEffect(() => {
    if (curtainProgress === config.curtainProgress.max) {
      sendTracking(tracking.curtainStatus, { status: config.tracking.curtainOpened });
    }
  }, [curtainProgress]);

  const handleCardsScroll = React.useCallback(
    throttle(
      150,
      (event: React.MouseEvent<HTMLDivElement>): void => {
        const target = event.target as HTMLDivElement;
        const { scrollLeft, offsetWidth } = target;
        const siteIndex = Math.floor(scrollLeft / offsetWidth);

        if (!sites[siteIndex]) return;

        sendTracking(tracking.scrollNav, { site: sites[siteIndex].key });
        setSelectedSite(siteIndex);
      },
      { noLeading: true }
    ),
    []
  );

  const handleNavDotClick = React.useCallback((index: number) => {
    if (!sitesRef.current) return;

    sendTracking(tracking.sliderDotNav);
    sitesRef.current.scrollTo({ left: sitesRef.current.offsetWidth * index, behavior: 'smooth' });
  }, []);

  const renderSites = React.useCallback(() => {
    return sites.map((siteMeta) => {
      const Logo = siteMeta.logo;

      return (
        <Html.div className={[styles.site, styles[siteMeta.key], 'w-100 h-100']} key={siteMeta.key}>
          <BackgroundImage className={[styles.bg, 'w-100 h-100']} src={siteMeta.bg} />
          <Html.div className={styles.logoContainer}>
            <Logo className={styles.logo} />
            <Html.div className="text-center">
              <Link href={siteMeta.link} className={styles.link} typography="caption">
                {formatMessage(translation.visitSite)}
              </Link>
            </Html.div>
          </Html.div>
          <Html.div className={[styles.footer, 'px-4']}>
            <Html.div className={styles.description}>
              {formatMessage(siteMeta.description, {
                visitors: config.numVisitorsMillions,
              })}
            </Html.div>
          </Html.div>
        </Html.div>
      );
    });
  }, [formatMessage]);

  return (
    <Html.div
      className={[
        styles.notConvincedBanner,
        curtainProgress >= config.curtainProgress.mid && styles.curtainHalfWayOpen,
        curtainProgress === config.curtainProgress.max && styles.curtainFullyOpen,
        'w-100',
      ]}
      testId={testId.component}
      ref={bannerRef}
    >
      <Html.div className={[styles.sites, 'w-100 h-100']} onScroll={handleCardsScroll} ref={sitesRef}>
        {renderSites()}
      </Html.div>
      <NavigationDots
        activeIndex={selectedSite}
        total={sites.length}
        className={[styles.navDots, 'd-md-none']}
        theme="dark"
        onClick={handleNavDotClick}
      />
      <Html.div className={styles.shade} />
      <Html.div className={styles.curtain} ref={curtainRef} />
      <Html.div className={[styles.intro, 'px-6 w-100 h-100']} ref={introRef}>
        <Html.div className={styles.notConvincedYet}>
          {formatMessage(translation.notConvincedYet, {
            b: (text) => <Html.b className={styles.highlight}>{text}</Html.b>,
          })}
        </Html.div>
        <Html.div className={styles.largestNetworks}>{formatMessage(translation.largestNetworks)}</Html.div>
        <Html.div className={[styles.explore, 'mt-2']}>{formatMessage(translation.explore)}</Html.div>
      </Html.div>
    </Html.div>
  );
};

export type { NotConvincedBannerElement };
export default NotConvincedBanner;
